import {
  defined,
  ScreenSpaceEventType,
  ScreenSpaceEventHandler,
  createGuid,
  CallbackProperty,
  Cartesian3,
  SceneTransforms,
  Cartesian2,
} from "@cesium/engine";

import MeasurementLengthStraight from "../measurement/MeasurementLengthStraight";
import DitapAlert from "../popup/DitapAlert";
import Utils from "../basic/Utils";

/**
 * 직선 거리 측정을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input class for straight length measurement using mouse/keyboard input.
 *
 * @alias InteractionLengthStraight
 * @constructor
 *
 * @param {object} lengthStraightOption 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Function} [lengthStraightOption.startCallback] 첫 번째 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating first point.
 * @param {Function} [lengthStraightOption.addCallback] 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating a point.
 * @param {Function} [lengthStraightOption.endCallback] 마지막 점을 생성할 때 실행되는 콜백 함수. Callback function executed when creating last point.
 * @param {MeasurementLengthStraight}  [lengthStraightOption.measurementLengthStraight] 직선 거리 계산 및 영역 표시 클래스.<br> Straight distance calculation and area display class.
 * @example
 * // 샌드캐슬 예제 'DiTAP - Straight Length Measurement' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const measurementLengthStraight = new Ditap.MeasurementLengthStraight(viewer);
 *
 * // InteractionLengthStraight on/off 실행 시 MeasurementLengthStraight 초기화 필수
 * // measurementLengthStraight 초기화 방법
 * // 1. InteractionLengthStraight 생성 후, MeasurementLengthStraight, callback 함수 대입
 * const interactionLengthStraight = new Ditap.InteractionLengthStraight();
 * interactionLengthStraight.measurementLengthStraight = measurementLengthStraight;
 * interactionLengthStraight.startCallback = () => {};
 * interactionLengthStraight.addCallback = () => {};
 * interactionLengthStraight.endCallback = () => {};
 *
 * // 2. InteractionLengthStraight 생성 시, lengthStraightOption 지정
 * const lengthStraightOption = {
 *    measurementLengthStraight: measurementLengthStraight,
 *    startCallback: () => {}
 *    addCallback: () => {}
 *    endCallback: () => {}
 * };
 * const interactionLengthStraight = new Ditap.InteractionLengthStraight(lengthStraightOption);
 */
class InteractionLengthStraight {
  constructor(lengthStraightOption) {
    this._measurementLengthStraight = undefined; //원본 클래스
    if (
      lengthStraightOption &&
      lengthStraightOption.measurementLengthStraight instanceof
        MeasurementLengthStraight
    ) {
      this.measurementLengthStraight =
        lengthStraightOption.measurementLengthStraight; //원본 클래스
    }
    this._moveEntities = []; //move 시 생성되는 Entity Id 배열
    this._infoLabelEntity = undefined;
    this._mousePosition = undefined;
    this._handler = undefined;
    this._isOn = false; // 핸들러 실행 여부
    this._keyDownEvent = undefined;
    this._waringAlert = undefined;
    this._buttonElement = undefined;
    this._polylinePrimitive = undefined;
    this.cancelBtn = undefined; // 핸들러 취소 버튼
    this.removeBtn = undefined; // 한점제거 버튼
    this.endBtn = undefined; // 종료 버튼
    this._isMobile = undefined; // 모바일 여부

    if (lengthStraightOption) {
      if (lengthStraightOption.startCallback instanceof Function) {
        this._startCallback = lengthStraightOption.startCallback;
      } else {
        this._startCallback = undefined;
      }
      if (lengthStraightOption.addCallback instanceof Function) {
        this._addCallback = lengthStraightOption.addCallback;
      } else {
        this._addCallback = undefined;
      }
      if (lengthStraightOption.endCallback instanceof Function) {
        this._endCallback = lengthStraightOption.endCallback;
      } else {
        this._endCallback = undefined;
      }
    }
  }

  /**
   * 직선 거리 계산 및 영역 표시 클래스.<br>
   * Straight distance calculation and area display class.
   * @type {MeasurementLengthStraight}
   */
  set measurementLengthStraight(measurementLengthStraight) {
    if (
      measurementLengthStraight instanceof MeasurementLengthStraight ||
      measurementLengthStraight === undefined
    ) {
      this._measurementLengthStraight = measurementLengthStraight; //원본 클래스
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get measurementLengthStraight() {
    return this._measurementLengthStraight;
  }

  /*
   * 마우스 무브 시 생성된 엔티티 배열.</br>
   * An array of entities created by mouse movement.
   * @type {Entity}
   */
  set moveEntities(moveEntities) {
    this._moveEntities = moveEntities;
  }
  get moveEntities() {
    return this._moveEntities;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(boolean) {
    this._isOn = boolean;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 카메라 시점 변경 시 실행 이벤트. </br>
   * Events to run when changing camera viewpoint in window.
   * @type {Function}
   */
  set cameraChangedEvent(cameraChangedEvent) {
    this._cameraChangedEvent = cameraChangedEvent;
  }
  get cameraChangedEvent() {
    return this._cameraChangedEvent;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }
  get keyDownEvent() {
    return this._keyDownEvent;
  }

  /*
   * 경고 알림 요소.</br>
   * A waring alert.
   * @type {Element}
   */
  set waringAlert(waringAlert) {
    this._waringAlert = waringAlert;
  }
  get waringAlert() {
    return this._waringAlert;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }
  get buttonElement() {
    return this._buttonElement;
  }

  /*
   * 동적 폴리라인 primitive.</br>
   * Dynamic polyline primitive
   * @type {Primitive}
   */
  set polylinePrimitive(polylinePrimitive) {
    this._polylinePrimitive = polylinePrimitive;
  }
  get polylinePrimitive() {
    return this._polylinePrimitive;
  }

  /**
   * 첫 번째 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating first point.
   * @type {Function}
   */
  set startCallback(startCallback) {
    if (startCallback instanceof Function) {
      this._startCallback = startCallback;
    } else {
      this.startCallback = undefined;
    }
  }
  get startCallback() {
    return this._startCallback;
  }

  /**
   * 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating a point.
   * @type {Function}
   */
  set addCallback(addCallback) {
    if (addCallback instanceof Function) {
      this._addCallback = addCallback;
    } else {
      this.addCallback = undefined;
    }
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   * 마지막 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating last point.
   * @type {Function}
   */
  set endCallback(endCallback) {
    if (endCallback instanceof Function) {
      this._endCallback = endCallback;
    } else {
      this.endCallback = undefined;
    }
  }
  get endCallback() {
    return this._endCallback;
  }

  /**
   * 직선 거리 측정 핸들러 실행. (MeasurementLengthStraight 초기화 필수.)</br>
   * Execute straight length measurement handler. (MeasurementLengthStraight initialization is required.)
   */
  on() {
    if (this.measurementLengthStraight && !this.isOn) {
      this.isOn = true;
      this._isMobile = Utils.detectDeviceType();
      this.measurementLengthStraight.viewer.currentEvent = this;
      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      this.handler = new ScreenSpaceEventHandler(
        this.measurementLengthStraight.viewer.canvas
      );

      // 경고창 생성
      this.waringAlert = new DitapAlert({
        type: "warning",
        title: "경고",
        content1: "직선 거리 측정 시, ",
        content2: "두 점 이상의 점이 필요합니다",
        buttons: "ONLYOK",
        open: false,
        callback: () => {},
      });

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.measurementLengthStraight.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "왼 클릭 또는 터치하여",
        "직선거리를 측정할 선을 그려주세요",
        true,
        true
      );

      this.infoLabel = Utils.createInfoLabel(
        this.measurementLengthStraight.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "왼 클릭하여 직선거리를 측정할 선을",
        "그려주세요",
        true,
        true
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.endBtn = this.mobileInfoLabel.endBtn;
        this.removeBtn = this.mobileInfoLabel.removeBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.infoLabel.style.display = "none";
        this.createMobileEvent();
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      this.createLeftClickHandler();
      this.createDoubleClickHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createCameraMoveEvent();
      this.createResizeEvent();
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const viewer = this.measurementLengthStraight.viewer;

    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.endBtn = this.mobileInfoLabel.endBtn;
            this.removeBtn = this.mobileInfoLabel.removeBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            for (const entity of this.moveEntities) {
              viewer.entities.remove(entity);
            }
            this.moveEntities = [];

            if (this.polylinePrimitive) {
              viewer.scene.primitives.remove(this.polylinePrimitive);
            }

            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }

      this.changeLengthLabelPosition();
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.measurementLengthStraight.removeAllEntity();
        this.destroy();
      })
    );

    this.endBtn.addEventListener(
      "click",
      (this.endEvent = () => {
        this.endHandler();
      })
    );

    this.removeBtn.addEventListener(
      "click",
      (this.removeEvent = () => {
        this.removePoint();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
      this.cancelBtn = undefined;
      this.cancelEvent = undefined;
    }
    if (this.endEvent) {
      this.endBtn.removeEventListener("click", this.endEvent);
      this.endBtn = undefined;
      this.endEvent = undefined;
    }
    if (this.removeEvent) {
      this.removeBtn.removeEventListener("click", this.removeEvent);
      this.removeBtn = undefined;
      this.removeEvent = undefined;
    }
  }

  /*
   * 카메라 시점 이동 시 이벤트 생성. </br>
   * Create an event when moving the camera point of view.
   */
  createCameraMoveEvent() {
    const viewer = this.measurementLengthStraight.viewer;
    this.cameraChangedEvent = () => {
      this.changeLengthLabelPosition();
    };
    viewer?.scene.camera.changed.addEventListener(this.cameraChangedEvent);
  }

  /*
   * 길이 라벨 위치 변경. </br>
   * Change the position of the length label.
   */
  changeLengthLabelPosition() {
    const viewer = this.measurementLengthStraight.viewer;
    const labelEntities = this.measurementLengthStraight.labelEntities;
    const positions = this.measurementLengthStraight.positions;
    // 길이 라벨
    if (labelEntities.length > 0) {
      for (let i = 0; i < labelEntities.length; i++) {
        const height = this.measurementLengthStraight.labelHeight;

        Utils.changeLabelLocation(
          viewer,
          positions[i],
          labelEntities[i],
          height
        );
      }
    }
  }

  /**
   * 직선 거리 측정 핸들러 종료. (MeasurementLengthStraight 초기화 필수.)</br>
   * End straight length measurement handler. (MeasurementLengthStraight initialization is required.)
   */
  off() {
    //핸들러 실행 중인 경우
    if (this.measurementLengthStraight && this.isOn) {
      const viewer = this.measurementLengthStraight.viewer;
      viewer.currentEvent = undefined;

      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");
      // move 시 생성 되는 entity 제거
      for (const entity of this.moveEntities) {
        viewer.entities.remove(entity);
      }

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      // PRIMITIVE - 다이나믹 폴리라인 프리미티브 제거
      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
        window.removeEventListener("keydown", this.keyDownEvent);
      }

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      // 카메라 이벤트 삭제
      viewer.scene.camera.changed.removeEventListener(this.cameraChangedEvent);

      this.moveEntities = [];
      this.mousePosition = undefined;
      this.handler = undefined;
      this.isOn = false; // 핸들러 실행 여부
      this.cameraChangedEvent = undefined;
      this.keyDownEvent = undefined;
      this.mousemoveEvent = undefined;
      this.polylinePrimitive = undefined;
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;

      // alert창 삭제
      this.waringAlert.destroy();
      this.waringAlert = undefined;
      this.cancelBtn = undefined;
      this.endBtn = undefined;
      this.removeBtn = undefined;
      this._isMobile = undefined;
    }
  }

  /**
   * 측정 중 중단.</br>
   * Interrupt during measurement.
   */
  abort() {
    //measurementLengthStraight 삭제
    this.measurementLengthStraight?.destroy();
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const viewer = this.measurementLengthStraight.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = Utils.pickPosition(e.position, viewer);
      this.mousePosition = position;

      const positions = this.measurementLengthStraight.positions;

      let isEqual = false;
      if (positions.length > 0) {
        const lastPosition = positions[positions.length - 1];
        isEqual = Utils.isEqualPosition(lastPosition, position);
      }

      if (defined(position) && !isEqual) {
        positions.push(position);
        const pointOption = {
          viewer: viewer,
          id: createGuid(),
          position: position,
        };
        const point = Utils.createPoint(pointOption);
        this.measurementLengthStraight.pointEntities.push(point);

        // 점 생성 시 콜백함수 실행
        if (this.addCallback && this.measurementLengthStraight) {
          this.addCallback(this.measurementLengthStraight);
        }

        let length = 0;
        let totalLength = 0;
        if (positions.length > 1) {
          const array = [];
          array.push(positions[positions.length - 2]);
          array.push(positions[positions.length - 1]);
          length = this.measurementLengthStraight.calculateLength(array);
          totalLength = this.measurementLengthStraight.calculateLength(
            positions
          );

          const polylineOption = {
            viewer: viewer,
            id: createGuid(),
            positions: array,
          };
          const polyline = Utils.createPolyline(polylineOption);
          this.measurementLengthStraight.polylineEntities.push(polyline);
        } else if (
          positions.length === 1 &&
          this.startCallback &&
          this.measurementLengthStraight
        ) {
          // 첫번째 점 생성 시 콜백함수 실행
          this.startCallback(this.measurementLengthStraight);
        }

        // 길이 라벨 생성 & 라벨 위치 지정
        const label = Utils.createLabel(
          this.measurementLengthStraight.viewer,
          length,
          totalLength
        );
        const height = label.clientHeight;
        this.measurementLengthStraight.labelHeight = height;

        Utils.changeLabelLocation(viewer, position, label, height);
        this.measurementLengthStraight.labelEntities.push(label);
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.measurementLengthStraight.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = Utils.pickPosition(e.endPosition, viewer);
      const positions = this.measurementLengthStraight.positions;

      if (defined(position)) {
        this.mousePosition = position;
        // 도움말 라벨 좌표 이동
        Utils.changeLabelLocation(
          viewer,
          this.mousePosition,
          this.infoLabelEntity
        );

        // move 시 생성 된 entities 제거
        for (const entity of this.moveEntities) {
          viewer.entities.remove(entity);
        }
        this.moveEntities = [];

        if (positions.length > 0) {
          const polylineOption = {
            viewer: viewer,
            id: createGuid(),
            positions: new CallbackProperty(() => {
              const left = positions[positions.length - 1];
              const right = position;

              const leftCart = SceneTransforms.wgs84ToWindowCoordinates(
                viewer.scene,
                left
              );
              const rightCart = SceneTransforms.wgs84ToWindowCoordinates(
                viewer.scene,
                right
              );

              const distance = Cartesian2.distance(leftCart, rightCart);

              const resultArray = [left];
              if (distance < 100) {
                resultArray.push(left);
              } else {
                const middle = Cartesian3.lerp(
                  left,
                  right,
                  9 / 10,
                  new Cartesian3()
                );
                resultArray.push(middle);
              }

              return resultArray;
            }, false),
          };
          const polyline = Utils.createPolyline(polylineOption);
          this.moveEntities.push(polyline);
        }

        // PRIMITIVE - 다이나믹 폴리라인 프리미티브 제거
        if (this.polylinePrimitive) {
          viewer.scene.primitives.remove(this.polylinePrimitive);
        }

        if (positions.length > 0) {
          const array = [];
          array.push(positions[positions.length - 1]);
          array.push(position);
          const primitiveOption = {
            viewer: viewer,
            positions: array,
          };
          const polyline = Utils.createPolylinePrimitive(primitiveOption);
          this.polylinePrimitive = polyline;
        }
      } else {
        this.infoLabelEntity.classList.add("hide");
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 더블 클릭 이벤트 핸들러 생성.</br>
   * Create mouse double-click event handler.
   */
  createDoubleClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.endHandler();
    }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  }

  /*
   * 직선 거리 측정 종료.<br>
   * End straight length measurement.
   */
  endHandler() {
    const positions = this.measurementLengthStraight.positions;
    if (positions.length > 1) {
      // drawing entity 제거
      this.measurementLengthStraight.removeAllEntity();

      // 엔티티 생성
      this.measurementLengthStraight.createEntity();

      // 마지막 점 생성 시 콜백함수 실행
      if (this.endCallback && this.measurementLengthStraight) {
        this.endCallback(this.measurementLengthStraight);
      }
      this.off();
    } else {
      this.waringAlert.open();
    }
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = this.mousePosition;
      if (defined(position)) {
        const positions = this.measurementLengthStraight.positions;

        // 마지막 Entity 제거, 배열에서도 제거
        if (positions.length > 0) {
          this.removePoint();
        } else {
          this.abort();
          this.off();
        }
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      // ESC 클릭 : Entity 제거, 모든 핸들러 종료
      this.measurementLengthStraight.removeAllEntity();

      this.destroy();
    } else if (event.key === "Backspace") {
      this.removePoint();
    }
  }

  /*
   * 측정 중인 한 점 제거.<br>
   * Remove the point being measured.
   */
  removePoint() {
    const viewer = this.measurementLengthStraight.viewer;
    const moveEntities = this.moveEntities;
    const positions = this.measurementLengthStraight.positions;
    const pointEntities = this.measurementLengthStraight.pointEntities;
    const labelEntities = this.measurementLengthStraight.labelEntities;
    const polylineEntities = this.measurementLengthStraight.polylineEntities;

    // 마지막 Entity 제거, 배열에서도 제거
    if (positions.length > 0) {
      const point = pointEntities[pointEntities.length - 1];

      // 마지막 point 제거
      this.measurementLengthStraight.removeLastElement(pointEntities);
      this.measurementLengthStraight.removeViewerEntity(point);

      const label = labelEntities[labelEntities.length - 1];
      this.measurementLengthStraight.removeLastElement(labelEntities);
      label.remove();

      for (const entity of moveEntities) {
        viewer.entities.remove(entity);
      }

      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      if (positions.length > 1) {
        const polyline = polylineEntities[polylineEntities.length - 1];
        this.measurementLengthStraight.removeLastElement(polylineEntities);
        this.measurementLengthStraight.removeViewerEntity(polyline);
      }

      positions.pop();

      if (!this._isMobile && positions.length > 0) {
        const array = [];
        array.push(positions[positions.length - 1]);
        array.push(this.mousePosition);
        const polylineOption = {
          viewer: viewer,
          id: createGuid(),
          positions: new CallbackProperty(() => array, false),
        };
        const polyline = Utils.createPolyline(polylineOption);
        this.moveEntities.push(polyline);
      }
    }
  }
}

export default InteractionLengthStraight;
